import type { InboxState } from "@/lib/states/global-state"

export type AssignedSubTab = "open" | "snoozed" | "done" | "close-loop"

// Maps the sidebar tab plus the Open / Snoozed / Done / Close loop sub-tab to
// the tab used by the list query. "my" and "open" encode state in the tab
// itself; other tabs keep their identity and narrow via stateScope instead.
export function resolveEffectiveInboxTab(
	inboxState: InboxState,
	assignedSubTab: AssignedSubTab,
): InboxState {
	if (inboxState === "my") {
		if (assignedSubTab === "snoozed") return "my-snoozed"
		if (assignedSubTab === "done") return "my-done"
		if (assignedSubTab === "close-loop") return "my-close-loop"
		return "my"
	}
	if (inboxState === "open") {
		if (assignedSubTab === "snoozed") return "snoozed"
		if (assignedSubTab === "done") return "done"
		if (assignedSubTab === "close-loop") return "close-loop"
		return "open"
	}
	return inboxState
}

// A view that saved its own status set already answers open / snoozed / done,
// so a sub-tab on top of it would intersect the two and hide rows the view asks
// for (all statuses selected plus the Open sub-tab left only NEW).
export function viewOwnsStatusFilter(filters?: {
	statuses?: readonly unknown[] | null
}): boolean {
	return (filters?.statuses?.length ?? 0) > 0
}

// Open / Snoozed / Done / Close loop sub-tabs show on every built-in list tab
// and on an active custom view that has no status filter of its own.
export function showInboxSubTabs(
	inboxState: InboxState,
	hasActiveCustomView: boolean,
	viewOwnsStatuses = false,
): boolean {
	if (inboxState === "custom-view") {
		return hasActiveCustomView && !viewOwnsStatuses
	}
	return (
		inboxState === "new" ||
		inboxState === "my" ||
		inboxState === "open" ||
		inboxState === "close-loop" ||
		inboxState === "sla-breached" ||
		inboxState === "needs-response"
	)
}

// Tabs that already encode state in the effective tab name do not need a
// separate stateScope. Everything else that shows sub-tabs does.
export function resolveInboxStateScope(
	inboxState: InboxState,
	hasActiveCustomView: boolean,
	subTab: AssignedSubTab,
	viewOwnsStatuses = false,
): AssignedSubTab | undefined {
	if (inboxState === "custom-view" && hasActiveCustomView) {
		return viewOwnsStatuses ? undefined : subTab
	}
	if (
		inboxState === "my" ||
		inboxState === "open" ||
		inboxState === "close-loop"
	) {
		return undefined
	}
	if (
		inboxState === "new" ||
		inboxState === "sla-breached" ||
		inboxState === "needs-response"
	) {
		return subTab
	}
	return undefined
}

/** @deprecated Use resolveInboxStateScope */
export const resolveCustomViewStateScope = resolveInboxStateScope

// Close loop is a thread state, not a scope, so picking any other status while
// on that tab has to move the sidebar back to All.
export function sidebarTabForSubTabChange(
	inboxState: InboxState,
	value: AssignedSubTab,
): InboxState | null {
	if (inboxState === "close-loop" && value !== "close-loop") return "open"
	return null
}

// The sub-tab a sidebar tab change lands on. `pending` is set when the status
// dropdown moved the tab itself, so the picked status survives the switch.
export function resolveSubTabAfterTabChange({
	prev,
	next,
	pending,
	current,
}: {
	prev: InboxState
	next: InboxState
	pending: AssignedSubTab | null
	current: AssignedSubTab
}): AssignedSubTab {
	if (pending) return pending
	// Migrating the retired snoozed / done sidebar tabs into Open + sub-tab.
	if ((prev === "snoozed" || prev === "done") && next === "open") return current
	return next === "close-loop" ? "close-loop" : "open"
}

// Which threads the list currently shows. The sidebar tab alone is not enough:
// on tabs with sub-tabs the Snoozed / Done / Close loop sub-tab narrows what is
// on screen.
export function resolveInboxListKind(
	inboxState: InboxState,
	hasActiveCustomView: boolean,
	subTab: AssignedSubTab,
	viewOwnsStatuses = false,
): AssignedSubTab {
	const effectiveTab = resolveEffectiveInboxTab(inboxState, subTab)
	const scope = resolveInboxStateScope(
		inboxState,
		hasActiveCustomView,
		subTab,
		viewOwnsStatuses,
	)
	if (scope) return scope
	if (effectiveTab === "done" || effectiveTab === "my-done") return "done"
	if (effectiveTab === "snoozed" || effectiveTab === "my-snoozed") {
		return "snoozed"
	}
	if (effectiveTab === "close-loop" || effectiveTab === "my-close-loop") {
		return "close-loop"
	}
	return "open"
}
